import {ChangeEvent, useMemo} from "react";
import {
  SelectField,
  Button,
  Multiselect,
  FloatingFieldLabel,
  styled,
} from "@reside/ui";
import {Form, useFormikContext} from "formik";
import {connect} from "react-redux";
import {BackdropSpinner} from "../../atoms/spinner";
import {AppState, select} from "../../store";
import {DropdownTreeSelect} from "../../atoms/dropdown-tree-select";
import {ButtonLink} from "../../atoms/button-link";
import {useEventTriggerFormFields} from "./hooks/useEventTriggerFormFields";
import {useTestUpload} from "./hooks/useTestUpload";
import {EventTriggerForm} from "../../services/HubAndSpokeService";
import {UploadOnStates} from "../../utils/constants";
import {
  generatePathPreview,
  getEventTriggerFacilitiesTree,
  truncateAnswer,
} from "./helpers";
import {DocumentPaths} from "./DocumentPaths";
import {UPLOAD_ON_OPTIONS} from "./consts";
import {WorkflowType} from "../../utils/enums";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {SimpleFacilityModel} from "../../utils/types/facility";
import {
  FieldGrid,
  FooterActions,
  FormFooter,
  FormSection,
  FormSectionHint,
  FormSectionTitle,
  MissingFieldsHint,
} from "./styles";

const EVENT_TRIGGERS_PATH = "/admin/hub-and-spoke/event-triggers";

type Props = Readonly<{
  allFacilities: ReadonlyArray<SimpleFacilityModel> | null;
  onSubmit: (values: EventTriggerForm) => void;
  isLoading: boolean;
  /** Edit mode only: renders a Delete button in the footer. */
  onDelete?: () => void;
  isDeleting?: boolean;
}>;

export const EventTriggersFormFieldsRenderer = ({
  allFacilities,
  onSubmit,
  isLoading = false,
  onDelete,
  isDeleting = false,
}: Props): JSX.Element => {
  const {
    activateBackdrop,
    templateNames,
    setTemplateName,
    workflows,
    setWorkflowType,
    templateFields,
    pdfTemplates,
    facilities,
    setFacilities,
    assignedFacilityIds,
    sftpConfigs,
  } = useEventTriggerFormFields();

  const facilitiesTree = useMemo(
    () =>
      getEventTriggerFacilitiesTree(allFacilities ?? [], assignedFacilityIds),
    [allFacilities, assignedFacilityIds],
  );

  const {values, errors, setFieldValue} = useFormikContext<EventTriggerForm>();

  const {mutate: testUpload, isLoading: isTestingUpload} = useTestUpload();

  const missingFields = [
    !values.template_field_id && "template field",
    !values.document && "document",
    !facilities?.length && "facilities",
    !values.sftp_config_id && "SFTP config",
    errors.document_path && "valid target path",
    errors.upload_on && "upload on",
  ].filter(Boolean);

  const isBusy = isLoading || isDeleting;

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        onSubmit({...values, facilities});
      }}
    >
      <BackdropSpinner active={activateBackdrop}>
        <FormSection>
          <FormSectionTitle>Source</FormSectionTitle>
          <FormSectionHint>
            Upload the document when the selected answer is given in the
            template.
          </FormSectionHint>
          <FieldGrid>
            <SelectField
              options={templateNames.map(template => ({
                label: template,
                value: template,
              }))}
              label="Template"
              name="templateName"
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                setTemplateName(event.target.value);
                setWorkflowType(WorkflowType.ADMISSION);
                setFieldValue("workflowType", WorkflowType.ADMISSION);
                setFieldValue("template_field_id", "");
                setFieldValue("document", "");
              }}
            />
            {/* Template fields and documents are scoped to a workflow of the template */}
            {workflows.length > 1 && (
              <SelectField
                name="workflowType"
                label="Workflow"
                options={workflows.map(workflow => ({
                  label: getWorkflowLabel(workflow),
                  value: workflow,
                }))}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                  setWorkflowType(event.target.value);
                  setFieldValue("template_field_id", "");
                  setFieldValue("document", "");
                }}
              />
            )}
          </FieldGrid>
          {templateFields && (
            <FieldGrid>
              <SelectField
                name="template_field_id"
                label="Template Field"
                options={templateFields
                  .filter(templateField => templateField.is_available_in_rule)
                  .map(templateField => ({
                    label: templateField.friendly_name
                      ? templateField.friendly_name
                      : truncateAnswer(templateField.answer_translation_text),
                    value: templateField.id,
                  }))}
              />
              {pdfTemplates && (
                <SelectField
                  name="document"
                  label="Document"
                  options={pdfTemplates.map(pdfTemplate => ({
                    label: pdfTemplate,
                    value: pdfTemplate,
                  }))}
                />
              )}
            </FieldGrid>
          )}
        </FormSection>

        <FormSection>
          <FormSectionTitle>Scope</FormSectionTitle>
          <FieldGrid>
            <FieldBox>
              <FloatingFieldLabel visible uppercase>
                Facilities
              </FloatingFieldLabel>
              <FacilitiesTrigger>
                <DropdownTreeSelect
                  checkedKeys={facilities || []}
                  onCheck={(_checkedKeys, checkedLeafKeys) => {
                    setFacilities(checkedLeafKeys);
                  }}
                  treeData={facilitiesTree}
                  triggerLabel=""
                  placement="bottom-start"
                />
              </FacilitiesTrigger>
            </FieldBox>
            <FieldBox>
              <FloatingFieldLabel visible uppercase>
                Upload On
              </FloatingFieldLabel>
              <Multiselect
                name="upload_on"
                placeholder="select options"
                testId="upload-on-multiselect"
                options={UPLOAD_ON_OPTIONS}
                selected={
                  values.upload_on || [UploadOnStates.admissionApproval]
                }
                onChange={(selectedValues: ReadonlyArray<string>) => {
                  setFieldValue("upload_on", selectedValues);
                }}
              />
            </FieldBox>
          </FieldGrid>
        </FormSection>

        <FormSection>
          <FormSectionTitle>Destination</FormSectionTitle>
          <SftpRow>
            {sftpConfigs && (
              <SelectField
                name="sftp_config_id"
                label="SFTP Config"
                options={sftpConfigs.map(sftpConfig => ({
                  label: sftpConfig.name
                    ? `${sftpConfig.name} - ${sftpConfig.hostname}`
                    : sftpConfig.hostname,
                  value: sftpConfig.id,
                }))}
              />
            )}
            <Button
              color="primary"
              outline
              disabled={
                !values.sftp_config_id ||
                !!errors.document_path ||
                isTestingUpload
              }
              onClick={() =>
                testUpload({
                  sftp_config_id: values.sftp_config_id,
                  target_path: generatePathPreview(values.document_path),
                })
              }
            >
              {isTestingUpload ? "Uploading..." : "Test upload"}
            </Button>
          </SftpRow>
          <DocumentPaths />
        </FormSection>

        <FormFooter>
          {onDelete && (
            <Button color="danger" outline onClick={onDelete} disabled={isBusy}>
              Delete
            </Button>
          )}
          <FooterActions>
            {missingFields.length > 0 && (
              <MissingFieldsHint>
                Required: {missingFields.join(", ")}
              </MissingFieldsHint>
            )}
            <ButtonLink to={EVENT_TRIGGERS_PATH} disabled={isBusy}>
              Cancel
            </ButtonLink>
            <Button
              type="submit"
              color="primary"
              disabled={isBusy || missingFields.length > 0}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </FooterActions>
        </FormFooter>
      </BackdropSpinner>
    </Form>
  );
};

const FieldBox = styled.div`
  margin-bottom: ${({theme}) => theme.space[5]}px;
`;

/** Gives the facility tree trigger the same underline as the select fields. */
const FacilitiesTrigger = styled.div`
  padding: ${({theme}) => theme.space[2]}px 0;
  border-bottom: ${({theme}) => `1px solid ${theme.color.gray20}`};
`;

const SftpRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[4]}px;

  > label {
    flex: 1;
  }
`;

// Redux connection for accessing the facilities from the state, including the inactive ones (Reside admins only)
const mapStateToProps = (state: AppState) => ({
  allFacilities: select.adminSession.allFacilities(state),
});

export const EventTriggersFormFields = connect(mapStateToProps)(
  EventTriggersFormFieldsRenderer,
);
